import axios from 'axios';
import { has, isEmpty, last, sum, values } from 'lodash';
import { action, makeAutoObservable, observable, reaction, runInAction } from 'mobx';
import moment from 'moment';

import { PostHogFeatureFlagService } from '@/components/FeatureFlagProvider';
import getEnvironment from '@/config/environment';

import { resources } from './resources';

export interface IRtmPeriod {
  case: string;
  clinicianDiscipline: string;
  clinicianEmrId: string;
  cycles: {
    end: string;
    start: string;
  }[];
  emrId: string;
  end: string;
  patientUuid: string;
  start: string;
  uuid: string;
  isActive: boolean;
  reports?: Record<string, string | number | unknown>[];
}

export interface IRtmStore {
  patientUuid?: string;
  resource?: string; // TODO: maybe enum
  isIdle: boolean;

  setIsIdle(isIdle: boolean): void;

  setPatientUuid(uuid?: string): void;

  setResource(resource?: string): void;

  fetchTrackingTime(start: string, end: string): void;

  patientsTracking: { [uuid: string]: number };
  allPatientsTime: number;

  track(): boolean;

  rtmPeriodList: { [id: string]: IRtmPeriod[] };

  fetchRtmPeriodList(patientUuid: string): Promise<void>;

  getCurrentRtmPeriod(patientUuid: string): IRtmPeriod | undefined;

  createRtmPeriod(rtmPeriod: IRtmPeriod): void;

  deleteRtmPeriod(patientUuid: string, periodUuid: string): void;

  editRtmPeriod(rtmPeriod: IRtmPeriod): void;

  getRtmAndPrivatePayPeriodsByDates(
    patientUuid: string,
    timeLine: { start: string; end: string },
  ): IRtmPeriod[];

  rtmEnabled: boolean;
  readonly trackPeriodSeconds: number;
  isSameOrganization: boolean;
}

class PatientTimeCacheStore {
  private readonly patientsTimeMap: Map<string, number>;
  featureFlagService = PostHogFeatureFlagService.getInstance();
  private readonly disposer?: () => void;

  constructor() {
    makeAutoObservable(this);
    this.patientsTimeMap = observable.map<string, number>();
    const raw = localStorage.getItem('PatientTimeCacheStore.patientsTimeMap');
    if (raw) {
      for (const [k, v] of Object.entries(JSON.parse(raw))) this.patientsTimeMap.set(k, Number(v));
    }

    this.disposer = reaction(
      () => Array.from(this.patientsTimeMap.entries()),
      (entries) => {
        localStorage.setItem(
          'PatientTimeCacheStore.patientsTimeMap',
          JSON.stringify(Object.fromEntries(entries)),
        );
      },
      { delay: 250 },
    );
  }

  @action
  async updatePatientTime(patientUuid: string) {
    try {
      if (this.featureFlagService.isFeatureEnabled('is_live_rtm_monitor_enabled')) {
        const result = await axios.get<{
          patientUuid: string;
          seconds: number;
        }>(`/api/camel/patients/${patientUuid}/monitor_time`);
        this.setPatientTime(result.data.patientUuid, result.data.seconds);
      }
    } catch (e) {
      console.error(e);
    }
  }

  @action
  private setPatientTime(patientUuid: string, time: number) {
    this.patientsTimeMap.set(patientUuid, time);
  }

  getPatientTime(patientUuid: string): number {
    return this.patientsTimeMap.get(patientUuid) || 0;
  }

  dispose() {
    this.disposer?.();
  }
}

class RtmStoreCache {
  private static instance: PatientTimeCacheStore;

  static getInstance(): PatientTimeCacheStore {
    if (!RtmStoreCache.instance) {
      RtmStoreCache.instance = new PatientTimeCacheStore();
    }
    return RtmStoreCache.instance;
  }

  static resetInstance(): void {
    RtmStoreCache.instance = new PatientTimeCacheStore();
  }
}

export const cacheStore = RtmStoreCache.getInstance();

export default class RtmStoreSingleton {
  private static instance: RtmStore;

  static getInstance(): RtmStore {
    if (!RtmStoreSingleton.instance) {
      RtmStoreSingleton.instance = new RtmStore();
    }
    return RtmStoreSingleton.instance;
  }

  static resetInstance(): void {
    RtmStoreSingleton.instance = new RtmStore();
  }
}

class RtmStore implements IRtmStore {
  patientUuid: string | undefined = undefined;
  resource: string | undefined = undefined;
  allPatientsTime = 0;
  patientsTracking: { [uuid: string]: number } = {};
  rtmPeriodList = {} as { [id: string]: IRtmPeriod[] };
  isIdle = false;
  _rtmEnabled = false;
  _isSameOrganization = false;
  trackPeriodSeconds = 30;

  constructor() {
    if (!getEnvironment().isProd) console.log('RtmStore initialized');
    makeAutoObservable(this);
  }

  @action
  setPatientUuid(uuid?: string): void {
    this.patientUuid = uuid;
  }

  @action
  setResource(resource?: string): void {
    this.resource = resource;
  }

  @action
  async fetchTrackingTime(start: string, end: string) {
    const result = await axios.get('/api/track_activity/list', {
      params: { start, end },
    });
    runInAction(() => {
      this.allPatientsTime = sum(values(result?.data?.aggByResource));
    });
  }

  setIsIdle(isIdle: boolean) {
    this.isIdle = isIdle;
  }

  track() {
    if (this.patientUuid && this.resource !== resources.skip && !this.isIdle) {
      if (has(this.patientsTracking, this.patientUuid)) {
        this.patientsTracking[this.patientUuid] =
          this.patientsTracking[this.patientUuid] + this.trackPeriodSeconds;
      } else {
        this.patientsTracking[this.patientUuid] = this.trackPeriodSeconds;
      }

      axios.put('/api/track_activity', {
        patientUuid: this.patientUuid,
        resource: this.resource ?? resources.general,
        seconds: this.trackPeriodSeconds,
      });
      setTimeout(() => {
        if (this.patientUuid) {
          cacheStore.updatePatientTime(this.patientUuid);
        }
      }, 5000);

      return true;
    }

    return false;
  }

  @action
  async fetchRtmPeriodList(patientUuid: string) {
    const result = await axios.get<{ result: IRtmPeriod[] }>('/api/rtm/billing_period/list', {
      params: { patientUuid },
    });
    runInAction(() => {
      this.rtmPeriodList[patientUuid] = result?.data?.result;
    });
  }

  getCurrentRtmPeriod(patientUuid: string) {
    return this.rtmPeriodList[patientUuid]?.find((period) =>
      moment().isBetween(moment(period.start), moment(period.end)),
    );
  }

  @action
  async createRtmPeriod(rtmPeriod: IRtmPeriod) {
    const result = await axios.post('/api/rtm/billing_period', rtmPeriod);
    runInAction(() => {
      if (!this.rtmPeriodList[rtmPeriod.patientUuid]) {
        this.rtmPeriodList[rtmPeriod.patientUuid] = [];
      }
      this.rtmPeriodList[rtmPeriod.patientUuid].push(result?.data);
    });
  }

  @action
  async deleteRtmPeriod(patientUuid: string, periodUuid: string) {
    await axios.delete(`/api/rtm/billing_period/${periodUuid}`);
    runInAction(() => {
      this.rtmPeriodList[patientUuid] = this.rtmPeriodList[patientUuid]?.filter(
        (period) => period.uuid !== periodUuid,
      );
    });
  }

  @action
  async editRtmPeriod(rtmPeriod: IRtmPeriod) {
    const result = await axios.put(`/api/rtm/billing_period/${rtmPeriod.uuid}`, rtmPeriod);
    runInAction(() => {
      const rtmPeriodList = this.rtmPeriodList[rtmPeriod.patientUuid];
      const index = rtmPeriodList?.findIndex((period) => period.uuid === rtmPeriod.uuid);
      rtmPeriodList[index] = result.data;
    });
  }

  createPrivatePayPeriod(
    clinicianDiscipline: string,
    patientUuid: string,
    startDate: string,
    endDate: string,
  ): IRtmPeriod {
    return {
      case: '',
      clinicianDiscipline: clinicianDiscipline,
      clinicianEmrId: '',
      cycles: [],
      emrId: '',
      isActive: false,
      patientUuid: patientUuid,
      uuid: '',
      start: startDate,
      end: endDate,
    };
  }

  getRtmAndPrivatePayPeriodsByDates(patientUuid: string, timeLine: { start: string; end: string }) {
    const result: IRtmPeriod[] = [];
    const patientRtmList = (this.rtmPeriodList[patientUuid] ?? []).filter((rtm) => {
      return moment(rtm.start).isBetween(
        moment(timeLine.start),
        moment(timeLine.end),
        undefined,
        '[]',
      );
    });
    if (!isEmpty(patientRtmList)) {
      const shouldFillStartWithPrivatePayPeriod = moment(timeLine.start).isBefore(
        moment(patientRtmList[0].start).subtract(1, 'days'),
      );
      if (shouldFillStartWithPrivatePayPeriod) {
        result.push(
          this.createPrivatePayPeriod(
            patientRtmList[0].clinicianDiscipline,
            patientUuid,
            moment(timeLine.start).format('YYYY-MM-DD'),
            moment(patientRtmList[0].start).subtract(1, 'days').format('YYYY-MM-DD'),
          ),
        );
      }
      result.push(
        ...patientRtmList.reduce((payPeriodsTimeline, rtmPeriod) => {
          const shouldWithFillPrivatePayPeriod =
            payPeriodsTimeline.length > 0 &&
            moment(last(payPeriodsTimeline)?.end).add(1, 'days').isBefore(moment(rtmPeriod.start));
          if (shouldWithFillPrivatePayPeriod) {
            payPeriodsTimeline.push(
              this.createPrivatePayPeriod(
                patientRtmList[0].clinicianDiscipline,
                patientUuid,
                moment(last(payPeriodsTimeline)?.end).add(1, 'days').format('YYYY-MM-DD'),
                moment(rtmPeriod.start).subtract(1, 'days').format('YYYY-MM-DD'),
              ),
            );
          }
          payPeriodsTimeline.push(rtmPeriod);
          return payPeriodsTimeline;
        }, [] as IRtmPeriod[]),
      );
      const shouldFillEndWithPrivatePayPeriod = moment(timeLine.end).isAfter(
        moment(result[result.length - 1].end),
      );
      if (shouldFillEndWithPrivatePayPeriod) {
        result.push(
          this.createPrivatePayPeriod(
            patientRtmList[0].clinicianDiscipline,
            patientUuid,
            moment(result[result.length - 1].end)
              .add(1, 'days')
              .format('YYYY-MM-DD'),
            moment(timeLine.end).format('YYYY-MM-DD'),
          ),
        );
      }
    } else {
      return [
        this.createPrivatePayPeriod(
          '',
          patientUuid,
          moment(timeLine.start).format('YYYY-MM-DD'),
          moment(timeLine.end).format('YYYY-MM-DD'),
        ),
      ];
    }
    return result;
  }

  get rtmEnabled() {
    return this._rtmEnabled;
  }

  set rtmEnabled(enabled: boolean) {
    this._rtmEnabled = enabled;
  }

  get isSameOrganization() {
    return this._isSameOrganization;
  }

  set isSameOrganization(newFlag: boolean) {
    this._isSameOrganization = newFlag;
  }
}
